import React from "react";
import { motion } from "framer-motion";
import {
  Refrigerator,
  Snowflake,
  Flame,
  Microwave,
  ShoppingBag,
  ArrowUpRight,
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const OFFERUP_URL = "https://offerup.com/p/45236131";

const reveal = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.7, ease: "easeOut" },
};

function ServiceCard({ icon: Icon, titleKey, descKey, span, delay = 0, cta }) {
  const { t } = useLanguage();
  return (
    <motion.article
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, delay, ease: "easeOut" }}
      className={`card-surface group relative p-8 md:p-10 ${span || ""}`}
      data-testid={`service-card-${titleKey}`}
    >
      <div className="flex items-center justify-between mb-8">
        <div className="w-14 h-14 rounded-full border border-[rgba(212,175,55,0.4)] flex items-center justify-center bg-[rgba(212,175,55,0.06)] transition-all duration-500 group-hover:border-[#d4af37] group-hover:bg-[rgba(212,175,55,0.15)]">
          <Icon
            className="text-[#d4af37] transition-transform duration-500 group-hover:scale-110"
            size={26}
            strokeWidth={1.4}
          />
        </div>
        <ArrowUpRight
          size={18}
          className="text-white/30 group-hover:text-[#d4af37] transition-all duration-500 group-hover:translate-x-1 group-hover:-translate-y-1"
        />
      </div>
      <h3 className="font-serif-display text-2xl md:text-3xl text-white mb-3 leading-tight">
        {t(`services.${titleKey}.title`)}
      </h3>
      <p className="text-sm text-white/65 leading-relaxed max-w-md">
        {t(`services.${titleKey}.desc`)}
      </p>
      {cta && (
        <a
          href={OFFERUP_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="service-card-offerup-cta"
          className="inline-flex items-center gap-2 mt-6 text-xs tracking-[0.2em] uppercase text-[#d4af37] hover:text-[#ffd700] transition-colors"
        >
          {t(`services.${titleKey}.cta`)} <ArrowUpRight size={14} />
        </a>
      )}
    </motion.article>
  );
}

export default function Services() {
  const { t } = useLanguage();
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative section-pad bg-[#0a0a0a]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div {...reveal} className="max-w-2xl mb-14 lg:mb-20">
          <span className="text-overline">{t("services.overline")}</span>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-3 leading-tight">
            <span className="text-white">{t("services.title").split(" ")[0]} </span>
            <span className="gold-gradient-text italic">
              {t("services.title").split(" ").slice(1).join(" ")}
            </span>
          </h2>
          <p className="mt-5 text-white/65 text-base max-w-lg">
            {t("services.subtitle")}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-6 gap-6">
          <ServiceCard
            icon={Refrigerator}
            titleKey="fridge"
            span="md:col-span-3"
            delay={0}
          />
          <ServiceCard
            icon={Snowflake}
            titleKey="cooler"
            span="md:col-span-3"
            delay={0.08}
          />
          <ServiceCard
            icon={Flame}
            titleKey="stove"
            span="md:col-span-2"
            delay={0.16}
          />
          <ServiceCard
            icon={Microwave}
            titleKey="oven"
            span="md:col-span-2"
            delay={0.24}
          />
          <ServiceCard
            icon={ShoppingBag}
            titleKey="used"
            span="md:col-span-2"
            delay={0.32}
            cta
          />
        </div>
      </div>
    </section>
  );
}
